import { useEffect, useState } from "react";
import Login from "./Login";
import MainPage from "./MainPage";
import GenerationView from "./components/GenerationView";
import { PALETTES, useTheme } from "./theme";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

// Check once at module load — not inside the component — so hooks order is stable.
const IS_GENERATION_TAB = window.location.hash === "#/generate";

function App() {
  const { userkey, setColor, setMode, setUsername, setEmail, setUserkey, setUserid } = useTheme();
  // Restore the session on refresh: if theme context already has a userkey
  // (came back from localStorage), the user was signed in — treat them as
  // still authenticated so we don't bounce them to the login page.
  const [isAuthed, setIsAuthed] = useState(() => Boolean(userkey));


  const DEFAULT_COLOR = "blue";
  const DEFAULT_MODE = "light";

  const resetThemeToDefault = () => {
    setColor(DEFAULT_COLOR);
    setMode(DEFAULT_MODE);
  };

  const applyThemeFromPreferences = async (userkey) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}userpreferences/user/${userkey}/page/main`
      );
      if (!res.ok) {
        // No saved preferences yet for this user — use defaults.
        resetThemeToDefault();
        return;
      }
      const body = await res.json();
      const savedMode = body?.data?.settings?.mode;
      const savedColor = body?.data?.settings?.color || body?.data?.settings?.theme;
      setMode(savedMode === "dark" ? "dark" : DEFAULT_MODE);
      setColor(savedColor && PALETTES[savedColor] ? savedColor : DEFAULT_COLOR);
    } catch {
      // Network / parse error — fall back to defaults so the UI is never
      // left showing the previous user's accent color.
      resetThemeToDefault();
    }
  };

  const handleLogin = (user) => {
    setUsername(user?.username);
    setEmail(user?.emailid);
    setUserkey(user?.userkey);
    setUserid(user?.userid);
    setIsAuthed(true);
    if (user?.userkey) applyThemeFromPreferences(user.userkey);
    else resetThemeToDefault();
  };

  // On mount: if the session was restored from localStorage (userkey present
  // + isAuthed true), re-fetch server-side preferences so a theme changed on
  // another device flows through. If nothing is stored, keep the default.
  useEffect(() => {
    if (isAuthed && userkey) {
      applyThemeFromPreferences(userkey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    setUsername(undefined);
    setEmail(undefined);
    setUserkey(undefined);
    setUserid(undefined);
    resetThemeToDefault();
    setIsAuthed(false);
  };

  // Generation tab: render standalone GenerationView (no auth needed — config
  // comes from localStorage set by the opener tab).
  if (IS_GENERATION_TAB) {
    return <GenerationView />;
  }

  if (!isAuthed) {
    return <Login onLogin={handleLogin} />;
  }

  return <MainPage onLogout={handleLogout} />;
}

export default App;
